import { createSignal, onMount, Show, onCleanup, Component } from "solid-js";
import { createStore } from "solid-js/store";
import { UserSession, Artwork } from "./types";
import { A } from "./components/universal/a-href";
import { Img } from "./components/universal/image";
import { TimelineTab } from "./tabs/timeline";
import { RelationshipsTab } from "./tabs/relations";
import { DepictedPlacesTab } from "./tabs/depicted-places";
import { ArtworkImageSection } from "./artwork-image-section";
import { DetailsTab } from "./tabs/details";
import { EmbeddedResourcesTab } from "./tabs/embedded-resources";
import { MoreImagesTab } from "./tabs/more-images";
import { AdminTab } from "./tabs/admin/admin";
import { SimilarTitlesTab } from "./tabs/similar-titles";
import "./assets/skeleton.css";
import { EntitiesTab } from "./tabs/entities";
import { fetchGet } from "./API";
import { GoogleMapsProvider } from "./google-maps-provider";

export const [session, setSession] = createStore<UserSession>({
    isLoggedIn: false,
    canAccessAdmin: false,
    member: {
        id: 0, firstName: "", lastName: "", permissions: [],
        memberType: "", username: ""
    }
});
type ArtworkListItem = {
    Id: number;
    Title: string;
    DateRange: string;
    ImageUrl: string;
    ImageHeight: number;
    ImageWidth: number;
}
type State = {
    isContentReady: boolean;
    currentArtworkIndex: number;
}
const hasPermission = (permission: string) => {
    if (!session.isLoggedIn) return false;
    return session.member?.permissions?.some(p => p == "Everything" || permission == p);
}
enum Direction {
    Previous,
    Next
}
type SideArtworkProps = {
    direction: Direction;
    item: ArtworkListItem;
}
const Previous = () => <><i class="fa fa-arrow-left" aria-hidden="true"></i> Previous</>;
const Next = () => <><i class="fa fa-arrow-right" aria-hidden="true"></i> Next</>;
const DirectionHeading = ({ direction }: { direction: Direction }) => 
    <>
        <Show when={direction == Direction.Previous}><Previous /></Show>
        <Show when={direction == Direction.Next}><Next /></Show>
    </>
const DetailsTabLoading : Component = () =>
<>
    <div class="didactic skeleton-card" style="width: 100%; margin: 0; background-color: #fff;">
        <div style="text-align: center;">
            <div class="skeleton-item skeleton-signature"></div>
        </div>
        <div class="artist-name">
            <div class="skeleton-item skeleton-title"></div>
        </div>
        <div class="artist-years">
            <div class="skeleton-item skeleton-years"></div>
        </div>
        <div>
            <div class="skeleton-item skeleton-description"></div>
        </div>
        <div class="artwork-title">
            <div class="skeleton-item skeleton-artwork-title"></div>
        </div>
        <div class="artwork-dates">
            <div class="skeleton-item skeleton-artwork-year"></div>
        </div>
        <div class="artwork-properties">
            <div class="skeleton-item skeleton-medium"></div>
        </div>
    </div>
</>;
const ArtworkImageSectionLoading : Component = () =>
<>
    <div style="width: 800px; margin: 0 auto; background-color: #000;">
        <div style="height: 600px;"></div>
        <div style="color: #efefef; padding: 10px 0; ">
            <div style="display: inline-block; margin-left: 10px; vertical-align: bottom;">
                <span class="fa fa-search-plus"></span>
            </div>
            <div style="display: inline-block; margin-left: 5px; vertical-align: bottom;">
                <p>
                    <span style="color: #efefef; text-decoration: underline;">View</span><br />
                </p>
            </div>
            <div style="display: inline-block; margin-left: 10px; vertical-align: middle;">
                <div style="margin: 0;">
                    <div class="skeleton-item skeleton-artwork-year"></div>
                </div>
            </div>
            <div style="display: inline-block; margin-left: 5px; font-size: 0.75rem; vertical-align: middle;">
                <div style="margin: 0;">
                    <div class="skeleton-item skeleton-artwork-year"></div>
                </div>
            </div>
            <div style="display: inline-block; margin-right: 15px; float: right; font-size: 0.75rem; vertical-align: middle;">
                <div style="margin: 0;">
                    <div class="skeleton-item skeleton-artwork-year"></div>
                </div>
                <div style="margin: 0;">
                    <div class="skeleton-item skeleton-artwork-year"></div>
                </div>
            </div>
        </div>
    </div>
</>;
const SideArtworkLoading : Component = () =>
<>
    <div style="width: 100%; background-color: #000; height: 300px;"></div>
    <div style="background-color:#efefef;padding:10px;text-align:center;"></div>
</>;
const SideArtwork : Component<SideArtworkProps> = ({ direction, item }) => 
    <>
        <div style="background-color: #000; padding: 10px; text-align: center; font-family: 'Oswald'; font-size: 1.5rem;">
            <a href={ `/artwork/index/${item.Id}` } style="color: #efefef"><DirectionHeading direction={direction} /></a>
        </div>
        <a href={ `/artwork/index/${item.Id}` }>
            <Img src={item.ImageUrl} mode="crop" width={400} anchor="topLeft" alt={item.Title} style="width: 100%;" />
        </a>
        <div style="background-color: #efefef; padding: 10px; text-align: center;">
            <div innerHTML={item.Title || ""} style="font-weight: 600;" />
        </div>
    </>
;
type Tab = "Details"|"Timeline"|"Admin"|"MoreImages"|"3DModels"|"Relationships"|"PlaceShown"|"SimilarTitles"|"Entities"|"Videos";
function App(props: any) {
    const [tab, setTab] = createSignal<Tab>("Details");
    const [artwork, setArtwork] = createStore<Artwork>({} as Artwork);
    const [artworkListItems, setArtworkListItems] = createStore<ArtworkListItem[]>([]);
    const [state, setState] = createStore<State>({ isContentReady: false, currentArtworkIndex: -1 });
    const getVideos = (artwork: Artwork) => artwork?.embeddedResources?.filter(x => x.embeddedUrl?.indexOf("YouTube") >= 0) || [];
    const getModels = (artwork: Artwork) => artwork?.embeddedResources?.filter(x => x.embeddedUrl?.indexOf("Sketchfab") >= 0) || [];
    const getArtwork = async () => {
        const response = await fetchGet("/Artwork/GetArtworkJson", { id: props.artworkId });
        const json = await response.json();
        return json;
    };
    const loadArtwork = async () => {
        const response = await getArtwork();
        if (!response.Success) return;
        setArtwork(response.Data);
    };
    const getAuthenticatedUser = async () => {
        const response = await fetch("/Forums/User/GetAuthenticatedUserJson");
        const json = await response.json();
        return json;        
    };
    const loadAuthenticatedUser = async () => {
        const response = await getAuthenticatedUser();
        if (!response.Success) return;
        const data = response.Data;
        setSession({
            isLoggedIn: true,
            canAccessAdmin: data.member?.canAccessAdmin,
            member: data.member
        } as UserSession);
    };
    const disableRightClick = (e: Event) => {
        const el = e.currentTarget as HTMLElement;
        if (el.className.indexOf("no-clicky-for-you") < 0) {
            return true;
        }
        if (e.type == "dragstart") {
            e.preventDefault();
        }
        if (e.type == "contextmenu") {
            return false;
        }
        return true;
    };
    const loadArtworkGallery = async () => {
        try {
            const data = {
                id: artwork.artist.id
            };
            const res = await fetchGet("/Artwork/GetArtworkListItemsJson", data);
            const json = await res.json();
            if (!json.Success) {
                return;
            }
            setArtworkListItems(json.Data);
            const i = artworkListItems.findIndex(x => x.Id == artwork.id);
            setState({ currentArtworkIndex: i });
        } catch(ex) {
            console.log("loadArtworkGallery", { ex });
        }
    }
    onMount(async () => {
        await loadAuthenticatedUser();
        await loadArtwork();
        await loadArtworkGallery();
        document.body.addEventListener("dragstart contextmenu", disableRightClick);
        setState({ isContentReady: true });
    });
    onCleanup(() => {
        document.body.removeEventListener("dragstart contextmenu", disableRightClick);
    });
    return (
        <section class="artwork">                
            <Show when={state.isContentReady && session.isLoggedIn}>
                <Show when={hasPermission("can-access-admin")}>
                    <div class="row">
                        <div class="col-md-12">
                            <div style="text-align: right;">
                                <A class="btn btn-large btn-default" href={ "/Admin/Artworks#/index/" + artwork.id }>
                                    Manage
                                </A>
                                <A class="btn btn-large btn-default" href={ "/Admin/Artworks#/add-for-artist/" + artwork.artist.id }>
                                    Add Artwork
                                </A>
                                <A class="btn btn-large btn-default" href={ "/Admin/Artworks#/add-image/" + artwork.id }>
                                    Upload Image
                                </A>
                            </div>
                        </div>
                    </div>
                </Show>
            </Show>
            <div class="row">
                <div class="col-md-12">
                    <Show when={state.isContentReady} fallback={<ArtworkImageSectionLoading />}>
                        <ArtworkImageSection artwork={artwork} />
                    </Show>
                    <div class="row" style="margin-top: 20px;">
                        <div class="col-md-2 hidden-xs hidden-sm">
                            <div style="width: 100%; text-align: center; margin-top: 52px;">
                                <Show when={state.isContentReady} fallback={<SideArtworkLoading />}>
                                    <Show when={state.currentArtworkIndex > 0}>
                                        <SideArtwork item={artworkListItems[state.currentArtworkIndex - 1]} direction={Direction.Previous} />
                                    </Show>
                                </Show>
                            </div>
                        </div>
                        <div class="col-md-8 col-xs-12 col-sm-12">
                            <section class="tabs">
                                <ul class="list-inline tablinkgroup unselectable">
                                    <li onClick={() => setTab("Details")} classList={{ ["active"]: tab() == "Details"}}>
                                        <span class="tablink">
                                            Details
                                        </span>
                                    </li>
                                    <Show when={state.isContentReady && artwork?.otherImages?.length}>
                                        <li onClick={() => setTab("MoreImages")} classList={{ ["active"]: tab() == "MoreImages"}}>
                                            <span class="tablink">
                                                More Images
                                            </span>
                                        </li>
                                    </Show>
                                    <Show when={state.isContentReady && getModels(artwork).length}>
                                        <li onClick={() => setTab("3DModels")} classList={{ ["active"]: tab() == "3DModels"}}>
                                            <span class="tablink">
                                                3D Models
                                            </span>
                                        </li>
                                    </Show>
                                    <Show when={state.isContentReady && getVideos(artwork).length}>
                                        <li onClick={() => setTab("Videos")} classList={{ ["active"]: tab() == "Videos"}}>
                                            <span class="tablink">
                                                Videos
                                            </span>
                                        </li>
                                    </Show>
                                    <Show when={state.isContentReady && artwork.depictedAddress}>
                                        <li onClick={() => setTab("PlaceShown")} classList={{ ["active"]: tab() == "PlaceShown"}}>
                                            <span class="tablink">
                                                Place Shown
                                            </span>
                                        </li>
                                    </Show>
                                    <Show when={state.isContentReady && artwork.hasRelationships}>
                                        <li onClick={() => setTab("Relationships")} classList={{ ["active"]: tab() == "Relationships"}}>
                                            <span class="tablink">
                                                Relationships
                                            </span>
                                        </li>
                                    </Show>
                                    <li onClick={() => setTab("Timeline")} classList={{ ["active"]: tab() == "Timeline"}}>
                                        <span class="tablink">
                                            Timeline
                                        </span>
                                    </li>
                                    <li onClick={() => setTab("SimilarTitles")} classList={{ ["active"]: tab() == "SimilarTitles"}}>
                                        <span class="tablink">
                                            Similar Titles
                                        </span>
                                    </li>
                                    <Show when={state.isContentReady && artwork.hasEntities}>
                                        <li onClick={() => setTab("Entities")} classList={{ ["active"]: tab() == "Entities"}}>
                                            <span class="tablink">
                                                Entities
                                            </span>
                                        </li>
                                    </Show>
                                    <Show when={session.isLoggedIn && hasPermission("can-access-admin")}>
                                        <li onClick={() => setTab("Admin")} classList={{ ["active"]: tab() == "Admin"}}>
                                            <span class="tablink">
                                                Admin
                                            </span>
                                        </li>
                                    </Show>
                                </ul>
                                <div class="tabgroup">
                                    <Show when={tab() == "Details"}>
                                        <div class="tab-pane active">
                                            <Show when={state.isContentReady} fallback={<DetailsTabLoading />}>
                                                <DetailsTab artwork={artwork} session={session} />
                                            </Show>
                                        </div>
                                    </Show>
                                    <Show when={tab() == "Entities"}>
                                        <div class="tab-pane active">                                
                                            <EntitiesTab artwork={artwork} />
                                        </div>
                                    </Show>
                                    <Show when={tab() == "3DModels"}>
                                        <div class="tab-pane active">                                
                                            <EmbeddedResourcesTab resources={artwork.embeddedResources} />
                                        </div>
                                    </Show>
                                    <Show when={tab() == "Videos"}>
                                        <div class="tab-pane active">                                
                                            <EmbeddedResourcesTab resources={artwork.embeddedResources} />
                                        </div>
                                    </Show>
                                    <Show when={tab() == "Relationships"}>
                                        <div class="tab-pane active">                                
                                            <RelationshipsTab data={artwork} />
                                        </div>
                                    </Show>
                                    <Show when={tab() == "SimilarTitles"}>
                                        <div class="tab-pane active">                                
                                            <SimilarTitlesTab artwork={artwork} />
                                        </div>
                                    </Show>
                                    <Show when={tab() == "PlaceShown"}>
                                        <GoogleMapsProvider apiKey="AIzaSyAweLj0DgOj1Em6hgX0-yPuOmwlvXLCJ2c">
                                            <div class="tab-pane active">                                
                                                <DepictedPlacesTab data={artwork} onBackToArtworksClicked={() => setTab("Details")} />
                                            </div>
                                        </GoogleMapsProvider>
                                    </Show>
                                    <Show when={tab() == "MoreImages"}>
                                        <div class="tab-pane active">                                
                                            <MoreImagesTab artwork={artwork} />
                                        </div>
                                    </Show>
                                    <Show when={tab() == "Timeline"}>
                                        <div class="tab-pane active">
                                            <div class="didactic" style="margin: 0; width: 100%; background-color: #fff;">
                                                <TimelineTab artwork={artwork} />
                                            </div>
                                        </div>
                                    </Show>
                                    <Show when={tab() == "Admin"}>
                                        <div class="tab-pane active">
                                            <div class="didactic" style="margin: 0; width: 100%; background-color: #fff;">
                                                <AdminTab artwork={artwork} />
                                            </div>
                                        </div>
                                    </Show>
                                </div>
                            </section>
                        </div>
                        <div class="col-md-2 hidden-xs hidden-sm">
                            <div style="width: 100%; text-align: center; margin-top: 52px;">
                                <Show when={state.isContentReady} fallback={<SideArtworkLoading />}>
                                    <Show when={state.currentArtworkIndex < artworkListItems.length - 2}>
                                        <SideArtwork item={artworkListItems[state.currentArtworkIndex + 1]} direction={Direction.Next} />
                                    </Show>
                                </Show>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
export default App;